/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
// Microsoft Clarity — session recordings, click and scroll heatmaps, rage clicks
// and dead clicks.
//
// One Clarity project serves every Omnetic app, every environment and every
// tenant; recordings are told apart by the tags set from here, which is why the
// tag vocabulary lives in one shared place instead of in each app.
//
// Whether Clarity runs at all is decided by the `script_clarity` feature flag,
// which is set per Flagsmith environment.
import {isNotNil} from 'ramda';

import {isRunningInCypress} from '../constants/isRunningInCypress';

/**
 * Public identifier — it is part of the script URL that every browser
 * downloads, so it is a constant rather than a build-time variable.
 */
const CLARITY_PROJECT_ID = 'ykax93hd9y';

export const CLARITY_SCRIPT_URL = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;

type ClarityCommand = (...args: unknown[]) => void;
type ClarityQueue = ClarityCommand & {q?: unknown[][]};

declare global {
  interface Window {
    clarity?: ClarityQueue;
  }
}

/**
 * The tags every Omnetic app sends, so that a recording can be found the same
 * way no matter which app produced it.
 *
 * - `app` – which application the session happened in (`dms`, `price-report`)
 * - `environment` – `prod`, `stage`, `dev`; keeps one project usable for all
 * - `workspace` – the tenant, the main axis we slice recordings by
 * - `module` – the product area currently open, such as sourcing or vehicles
 * - `user_role` – the roles the user holds, to tell a mechanic from an admin
 */
export type ClarityTag = 'app' | 'environment' | 'module' | 'workspace' | 'user_role';

/**
 * Queues Clarity commands issued before the script has finished downloading —
 * the same shim the official Clarity snippet installs. Without it every tag set
 * during the first render would be dropped.
 */
function ensureClarityQueue() {
  if (isNotNil(window.clarity)) {
    return;
  }

  const clarity: ClarityQueue = function (...args: unknown[]) {
    clarity.q = clarity.q ?? [];
    clarity.q.push(args);
  };

  window.clarity = clarity;
}

/**
 * Attaches a filterable tag to the current Clarity session. Several values may
 * be passed for one tag, so a user with more than one role stays findable under
 * each of them.
 */
export function setClarityTag(tag: ClarityTag, value: string | string[]) {
  ensureClarityQueue();
  window.clarity?.('set', tag, value);
}

/**
 * Ties the recording to a user. Takes the internal user id and never an e-mail
 * address — handing e-mails to a third party is exactly what T20-140250 removes
 * from the analytics layer.
 */
export function identifyClarityUser(userId: string) {
  ensureClarityQueue();
  window.clarity?.('identify', userId);
}

/**
 * Loads the Clarity script. Safe to call more than once. Apps that have the
 * `useExternalScript` hook should use that instead.
 */
export function loadClarityScript() {
  // The real Clarity runtime mutates the page asynchronously and currently
  // throws internally in Cypress ("reading 'sequence' of null"). Analytics
  // must never make a product E2E test fail, so do not download third-party
  // telemetry while Cypress is controlling the page.
  if (isRunningInCypress()) {
    return;
  }

  ensureClarityQueue();

  if (isNotNil(document.querySelector(`script[src="${CLARITY_SCRIPT_URL}"]`))) {
    return;
  }

  const script = document.createElement('script');
  script.src = CLARITY_SCRIPT_URL;
  script.defer = true;
  script.onerror = (error) => {
    // Clarity is a non-critical third-party script; ad-blockers, corporate
    // proxies and Clarity outages produce a steady stream of load failures that
    // are not actionable. Log a warning instead of reporting to Sentry.
    console.warn('Clarity script failed to load', error);
  };

  document.body.appendChild(script);
}
